// Audit log (FD §24.2) — append-only record of who did what. List + filter
// only: there is no edit/delete affordance anywhere, by design.

import { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { auditApi } from '../lib/api-client'
import {
  EmptyState,
  ErrorNote,
  LoadingRows,
  PageHeader,
} from '../components/ui'
import { sceneClass } from '../components/goblin-art'
import { useWorkspaceId } from '../lib/auth'
import { formatDateTime, humanize, shortId } from '../lib/format'

const ENTITY_TYPES = [
  'run',
  'task',
  'approval',
  'agent',
  'role',
  'skill',
  'project',
  'credential',
  'executor',
  'workspace',
]

/** Bookkeeping fields that don't help a person reading the log. */
const HIDDEN_METADATA = new Set(['event', 'userAgent'])

/** Renders already-redacted metadata (FD §24.3) as short key: value pairs. */
function MetadataSummary({ metadata }: { metadata: unknown }) {
  if (!metadata || typeof metadata !== 'object')
    return <span className="text-ink-3">—</span>
  const entries = Object.entries(metadata as Record<string, unknown>)
    .filter(([key]) => !HIDDEN_METADATA.has(key))
    .slice(0, 4)
  if (entries.length === 0) return <span className="text-ink-3">—</span>
  return (
    <dl className="space-y-0.5 text-sm">
      {entries.map(([key, value]) => (
        <div key={key} className="flex min-w-0 gap-2">
          <dt className="shrink-0 text-ink-3">{humanize(key)}</dt>
          <dd className="truncate font-mono text-xs leading-5 text-ink-2">
            {typeof value === 'string' ? value : JSON.stringify(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function AuditPage() {
  const workspaceId = useWorkspaceId()
  const [entityType, setEntityType] = useState<string>('')

  const auditQuery = useInfiniteQuery({
    queryKey: ['audit-log', workspaceId, entityType],
    queryFn: ({ pageParam }) =>
      auditApi.list({
        workspaceId,
        entityType: entityType || undefined,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: workspaceId !== '',
  })
  const entries = auditQuery.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div>
      <PageHeader
        art="auditor"
        title="Audit log"
        description="Every decision, change and login, in order. Entries can’t be edited or deleted."
        actions={
          <label className="flex items-center gap-2 text-sm text-ink-3">
            Show
            <select
              className="field-input h-8 w-auto py-0"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            >
              <option value="">Everything</option>
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanize(type)}s
                </option>
              ))}
            </select>
          </label>
        }
      />
      <div className={`panel overflow-x-auto ${sceneClass('archive')}`}>
        {auditQuery.isLoading ? (
          <LoadingRows />
        ) : auditQuery.isError ? (
          <ErrorNote error={auditQuery.error} what="the audit log" />
        ) : entries.length === 0 ? (
          <EmptyState title="No entries">
            {entityType
              ? 'Nothing recorded for this kind of item yet.'
              : 'Actions show up here as soon as anyone does something.'}
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="whitespace-nowrap align-top">
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td className="align-top">
                    {entry.actorName ? (
                      <span className="text-ink">{entry.actorName}</span>
                    ) : entry.actorUserId ? (
                      <span className="mono-id">
                        {shortId(entry.actorUserId)}
                      </span>
                    ) : (
                      (entry.actorLabel ?? 'Goblic')
                    )}
                  </td>
                  <td className="align-top">
                    <span className="block font-medium text-ink">
                      {entry.summary ?? humanize(entry.eventType)}
                    </span>
                    {entry.targetId ? (
                      <span className="block text-sm text-ink-3">
                        {humanize(entry.targetType)}{' '}
                        <span className="font-mono text-xs">
                          {shortId(entry.targetId)}
                        </span>
                      </span>
                    ) : null}
                  </td>
                  <td className="max-w-sm align-top">
                    <MetadataSummary metadata={entry.metadata} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {auditQuery.hasNextPage ? (
          <div className="flex justify-center border-t border-line px-5 py-3">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => void auditQuery.fetchNextPage()}
              disabled={auditQuery.isFetchingNextPage}
            >
              {auditQuery.isFetchingNextPage ? 'Loading…' : 'Show older'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
